import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import useAIStore from '../../stores/aiStore';
import useFileStore from '../../stores/fileStore';
import usePendingChangesStore from '../../stores/pendingChangesStore';
import { api } from '../../../convex/_generated/api';
import { parseMarkdown } from '../../utils/markdown';
import DOMPurify from 'dompurify';
import { AUTHOR_PERSONAS, DEFAULT_AUTHOR_ID } from '../../services/ai';
import { AI_MODEL_TIERS } from '../../constants/aiTiers';
import {
    createPendingAttachment,
    finalizePendingAttachment,
    revokeAttachmentPreview
} from '../../utils/chatAttachments';
import { formatAuthorBotMemory } from '../../utils/authorBotMemory';
import { compareProjectFilesByOrder } from '../../../shared/projectFileOrder';
import { selectRecentChapterFiles } from '../../../shared/recentChapterContext';
import { buildStoryBiblePromptContext } from '../../../shared/storyBiblePrompt';
import {
    CHALLENGE_PROJECT_DRAFT_PATH,
    CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH,
    CHALLENGE_PROJECT_SUBMISSION_PATH,
} from '../../../shared/challengeProjectSeed';
import {
    extractExplicitProjectPaths,
    findCanonicalProjectPath,
} from '../../../shared/projectPathMentions';
import { useTranslation } from 'react-i18next';
import { formatChatTimestamp } from '../../utils/i18nHelpers';
import { QUICK_PROMPTS } from '../../constants/quickPrompts';
import RealtimeVoiceChat from './RealtimeVoiceChat';
import './AssistantPanel.css';

function AssistantPanel({ projectId }) {
    const { t, i18n } = useTranslation();
    const {
        backendMode,
        chats,
        activeChatId,
        messages,
        isLoading,
        isStreaming,
        streamingMessage,
        sendMessage,
        stopGeneration,
        clearChat,
        selectedPersona,
        setSelectedPersona,
        loadMessages,
        createChat,
        renameChat,
        deleteChat,
        selectedTier,
        setSelectedTier,
        allowAgentEdits,
        setAllowAgentEdits,
        thinkingPhase,
        thinkingStartedAt,
    } = useAIStore();

    const { files, activeTabPath, getFileContent, openFile } = useFileStore();
    const { addPendingChange } = usePendingChangesStore();

    const [input, setInput] = useState('');
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
    const [showPersonaMenu, setShowPersonaMenu] = useState(false);
    const [showChatList, setShowChatList] = useState(false);
    const [showMentionMenu, setShowMentionMenu] = useState(false);
    const [mentionQuery, setMentionQuery] = useState('');
    const [mentionIndex, setMentionIndex] = useState(0);
    const [chatSearch, setChatSearch] = useState('');
    const [hoveredChatId, setHoveredChatId] = useState(null);
    const [editingChatId, setEditingChatId] = useState(null);
    const [editingTitle, setEditingTitle] = useState('');
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const sendLockRef = useRef(false);
    const fileInputRef = useRef(null);
    const pendingAttachmentsRef = useRef([]);
    const chatListRef = useRef(null);
    const editInputRef = useRef(null);
    const project = useQuery(api.projects.getById, projectId ? { projectId } : 'skip');
    const storyBible = useQuery(api.storyBibles.getCurrent, projectId ? { projectId } : 'skip');
    const storyBibleEntries = useQuery(api.storyBibleEntries.list, projectId ? { projectId } : 'skip');
    const chapterSummaries = useQuery(api.chapterSummaries.listByProject, projectId ? { projectId } : 'skip');
    const continuityStatus = useQuery(api.fileContinuity.getProjectStatus, projectId ? { projectId } : 'skip');
    const mentorMemory = useQuery(api.authorBotMemory.listByProject, projectId ? { projectId } : 'skip');
    const customMentors = useQuery(api.authorBots.listCustomAuthorBots, {}) || [];
    const createMentorMemory = useMutation(api.authorBotMemory.create);
    const deleteMentorMemory = useMutation(api.authorBotMemory.remove);
    const [showMemoryPanel, setShowMemoryPanel] = useState(false);
    const [showMemoryComposer, setShowMemoryComposer] = useState(false);
    const [showContextPanel, setShowContextPanel] = useState(false);
    const [memoryKind, setMemoryKind] = useState('canon_correction');
    const [memoryTitle, setMemoryTitle] = useState('');
    const [memoryAssumption, setMemoryAssumption] = useState('');
    const [memoryCorrection, setMemoryCorrection] = useState('');
    const [thinkingElapsed, setThinkingElapsed] = useState(0);
    const [quickPromptInput, setQuickPromptInput] = useState(null);
    const [quickPromptValue, setQuickPromptValue] = useState('');
    const [showVoiceChat, setShowVoiceChat] = useState(false);

    const currentChat = useMemo(
        () => chats.find((chat) => chat._id === activeChatId) || null,
        [chats, activeChatId],
    );

    const filteredChats = useMemo(() => {
        if (!chatSearch.trim()) return chats;
        const q = chatSearch.toLowerCase();
        return chats.filter((chat) => chat.title.toLowerCase().includes(q));
    }, [chats, chatSearch]);

    useEffect(() => {
        pendingAttachmentsRef.current = pendingAttachments;
    }, [pendingAttachments]);

    useEffect(() => {
        return () => {
            pendingAttachmentsRef.current.forEach(revokeAttachmentPreview);
        };
    }, []);

    const fileMentions = useMemo(() => {
        const query = mentionQuery.toLowerCase();
        return files
            .filter((file) => file.type === 'file')
            .filter((file) => !query || file.name.toLowerCase().includes(query) || file.path.toLowerCase().includes(query))
            .slice(0, 6);
    }, [mentionQuery, files]);

    const resolveMentionedFile = (rawPath) => {
        const canonicalPath = findCanonicalProjectPath(
            rawPath,
            files.map((file) => file.path),
        );
        if (!canonicalPath) {
            return null;
        }
        return files.find((file) => file.path === canonicalPath) || null;
    };

    const composerMentionFiles = useMemo(() => {
        return extractExplicitProjectPaths(input)
            .map((token) => resolveMentionedFile(token))
            .filter(Boolean);
    }, [input, files]);

    const authorBotMemory = useMemo(
        () => formatAuthorBotMemory(mentorMemory || []),
        [mentorMemory],
    );
    const storyBibleStatusLabel = storyBible?.summary
        ? t('ai.memory.loaded')
        : t('ai.memory.missing');
    const chapterSummaryCount = continuityStatus?.readyFiles ?? chapterSummaries?.length ?? 0;
    const savedMemoryCount = mentorMemory?.length || 0;
    const canonUpdateCount = continuityStatus?.pendingCanonUpdates ?? mentorMemory?.length ?? 0;
    const loadedContextSourceCount = [
        Boolean(storyBible?.summary),
        chapterSummaryCount > 0,
        savedMemoryCount > 0,
        canonUpdateCount > 0,
    ].filter(Boolean).length;
    const storyBibleContext = useMemo(
        () => buildStoryBiblePromptContext(storyBible),
        [storyBible],
    );
    const storyBibleEntityContext = useMemo(() => {
        if (!storyBibleEntries?.length) return '';
        return storyBibleEntries
            .slice(0, 40)
            .map((entry) => {
                const facts = (entry.quickFacts || [])
                    .slice(0, 4)
                    .map((fact) => `${fact.key}: ${fact.value}`)
                    .join(' | ');
                const aliases = entry.aliases?.length ? `Aliases: ${entry.aliases.slice(0, 4).join(', ')}` : '';
                const details = [entry.description, aliases, facts, entry.tags?.length ? `Tags: ${entry.tags.slice(0, 4).join(', ')}` : '']
                    .filter(Boolean)
                    .join(' | ');
                return `- ${entry.category}: ${entry.name}${details ? ` — ${details}` : ''}`;
            })
            .join('\n');
    }, [storyBibleEntries]);
    const orderedChapterFiles = useMemo(
        () => {
            const hasCanonicalChallengeSubmission = files.some((file) => file.type === 'file' && file.path === CHALLENGE_PROJECT_SUBMISSION_PATH);
            return files
                .filter((file) => {
                    if (file.type !== 'file' || typeof file.content !== 'string' || file.content.trim().length === 0) return false;
                    return (file.path.startsWith('Chapters/') && file.path !== CHALLENGE_PROJECT_DRAFT_PATH) ||
                        (!hasCanonicalChallengeSubmission && file.path === CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH);
                })
                .sort(compareProjectFilesByOrder);
        },
        [files],
    );
    const recentChapterFiles = useMemo(
        () =>
            selectRecentChapterFiles(orderedChapterFiles, activeTabPath, {
                previousCount: 1,
                fallbackCount: 1,
            }).map((file) => ({
                path: file.path,
                content: file.content || '',
            })),
        [orderedChapterFiles, activeTabPath],
    );
    const manuscriptChapterCount = orderedChapterFiles.length;
    const activeChapterIndex = orderedChapterFiles.findIndex((file) => file.path === activeTabPath);
    const activeChapterLabel = activeChapterIndex >= 0
        ? `${activeChapterIndex + 1}/${manuscriptChapterCount}`
        : t('common.none', { defaultValue: 'None' });

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, streamingMessage]);

    // Thinking elapsed timer
    useEffect(() => {
        if (!thinkingStartedAt || !isLoading) {
            setThinkingElapsed(0);
            return;
        }
        setThinkingElapsed(Math.floor((Date.now() - thinkingStartedAt) / 1000));
        const interval = setInterval(() => {
            setThinkingElapsed(Math.floor((Date.now() - thinkingStartedAt) / 1000));
        }, 1000);
        return () => clearInterval(interval);
    }, [thinkingStartedAt, isLoading]);

    // Close chat list when clicking outside
    useEffect(() => {
        if (!showChatList) return;
        const handler = (e) => {
            if (chatListRef.current && !chatListRef.current.contains(e.target)) {
                setShowChatList(false);
                setChatSearch('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showChatList]);

    // Focus edit input when editing
    useEffect(() => {
        if (editingChatId && editInputRef.current) {
            editInputRef.current.focus();
            editInputRef.current.select();
        }
    }, [editingChatId]);

    const resetComposer = (revokeAttachments = true) => {
        setInput('');
        setShowMentionMenu(false);
        setMentionQuery('');
        setMentionIndex(0);
        setPendingAttachments((prev) => {
            if (revokeAttachments) {
                prev.forEach(revokeAttachmentPreview);
            }
            return [];
        });
        if (inputRef.current) {
            inputRef.current.style.height = '';
        }
    };

    const syncTextareaHeight = (target) => {
        if (!target) return;
        target.style.height = 'auto';
        target.style.height = `${Math.min(target.scrollHeight, 140)}px`;
    };

    const handleAttachmentAdd = async (incomingFiles) => {
        if (!activeChatId || incomingFiles.length === 0) return;
        setIsUploadingAttachment(true);
        try {
            const prepared = [];
            for (const file of incomingFiles) {
                const isPdf = file.type === 'application/pdf';
                const isImage = file.type.startsWith('image/');
                if (!isPdf && !isImage) {
                    continue;
                }
                const attachment = await createPendingAttachment(file);
                prepared.push(attachment);
            }
            setPendingAttachments((prev) => [...prev, ...prepared]);
        } catch (error) {
            console.error('Attachment upload failed:', error);
        } finally {
            setIsUploadingAttachment(false);
        }
    };

    const handleFileInputChange = async (event) => {
        const nextFiles = Array.from(event.target.files || []);
        await handleAttachmentAdd(nextFiles);
        event.target.value = '';
    };

    const handleComposerPaste = async (event) => {
        const files = Array.from(event.clipboardData?.items || [])
            .filter((item) => item.kind === 'file')
            .map((item) => item.getAsFile())
            .filter(Boolean);
        const imageFiles = files.filter((file) => file.type.startsWith('image/'));
        if (imageFiles.length > 0) {
            event.preventDefault();
            await handleAttachmentAdd(imageFiles);
        }
    };

    const handleSend = async () => {
        if (sendLockRef.current || isLoading || isStreaming || !activeChatId) return;
        const normalizedInput = input.trim() || (
            pendingAttachments.length > 0
                ? t('ai.defaultAttachmentPrompt')
                : ''
        );
        if (!normalizedInput && pendingAttachments.length === 0) return;

        sendLockRef.current = true;

        const messageContent = normalizedInput;
        const localAttachments = [...pendingAttachments];
        const attachments = [];
        resetComposer(false);

        const currentContent = activeTabPath ? getFileContent(activeTabPath) : '';
        const mentionFiles = extractExplicitProjectPaths(messageContent)
            .map((token) => resolveMentionedFile(token))
            .filter(Boolean)
            .map((file) => ({
                path: file.path,
                content: file.content || '',
            }));

        const allFiles = files
            .filter((file) => file.type === 'file')
            .map((file) => ({
                path: file.path,
                content: file.content || '',
            }));

        const context = {
            projectName: project?.title,
            genre: project?.genre,
            synopsis: project?.synopsis,
            storyBibleContext,
            storyBibleEntityContext,
            storyBibleSummary: storyBible?.summary,
            authorBotMemory,
            activeFile: activeTabPath
                ? {
                    path: activeTabPath,
                    content: currentContent || '',
                }
                : undefined,
            recentChapterFiles,
            mentionFiles,
            allFiles,
            personaId: selectedPersona,
            attachments,
            onPendingChanges: async (changes) => {
                if (!Array.isArray(changes) || changes.length === 0) return;
                for (const change of changes) {
                    addPendingChange(
                        change.path,
                        change.originalContent || '',
                        change.newContent || '',
                        change.description || '',
                        {
                            isNewFile: Boolean(change.isNewFile),
                            name: change.name,
                            parentPath: change.parentPath,
                        }
                    );
                }

                const firstPath = changes[0]?.path;
                if (firstPath) {
                    openFile(firstPath);
                }
            }
        };

        try {
            for (const attachment of localAttachments) {
                attachments.push(
                    backendMode === 'local'
                        ? {
                            kind: attachment.kind,
                            name: attachment.name,
                            contentType: attachment.contentType,
                            size: attachment.size,
                            extractedText: attachment.extractedText,
                            url: attachment.url,
                            id: attachment.id,
                        }
                        : await finalizePendingAttachment(activeChatId, attachment)
                );
            }
            context.attachments = attachments;
            await sendMessage(projectId, messageContent, context);
        } finally {
            localAttachments.forEach(revokeAttachmentPreview);
            sendLockRef.current = false;
            requestAnimationFrame(() => {
                inputRef.current?.focus();
            });
        }
    };

    const handleKeyDown = (e) => {
        if (showMentionMenu) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setMentionIndex((prev) => Math.min(prev + 1, fileMentions.length - 1));
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setMentionIndex((prev) => Math.max(prev - 1, 0));
                return;
            }
            if (e.key === 'Enter' && fileMentions[mentionIndex]) {
                e.preventDefault();
                handleMentionSelect(fileMentions[mentionIndex]);
                return;
            }
        }

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void handleSend();
        }
    };

    const handleMentionSelect = (file) => {
        const cursorPos = inputRef.current?.selectionStart ?? input.length;
        const before = input.slice(0, cursorPos).replace(/@([^\s]*)$/, `@{${file.path}}`);
        const after = input.slice(cursorPos);
        const next = `${before}${after}`;
        setInput(next);
        setShowMentionMenu(false);
        setMentionQuery('');
        setMentionIndex(0);
        requestAnimationFrame(() => {
            inputRef.current?.focus();
            syncTextareaHeight(inputRef.current);
        });
    };

    useEffect(() => {
        const handler = (event) => {
            const detail = event.detail || '';
            setInput((prev) => {
                const next = prev ? `${prev}\n${detail}` : detail;
                requestAnimationFrame(() => syncTextareaHeight(inputRef.current));
                return next;
            });
            requestAnimationFrame(() => {
                inputRef.current?.focus();
            });
        };
        window.addEventListener('ai:addToChat', handler);
        return () => window.removeEventListener('ai:addToChat', handler);
    }, []);

    useEffect(() => {
        resetComposer();
    }, [activeChatId]);

    const personaOptions = useMemo(() => {
        const builtIn = Object.values(AUTHOR_PERSONAS);
        const custom = customMentors.map((mentor) => ({
            id: `custom:${mentor._id}`,
            name: mentor.title || t('onboarding.stepThree.customMentor.fallbackName'),
            description: mentor.description || t('onboarding.stepThree.customMentor.fallbackDescription'),
            emoji: mentor.emoji || '🧠',
        }));
        return [...builtIn, ...custom];
    }, [customMentors, t]);

    const personaMap = useMemo(
        () => new Map(personaOptions.map((persona) => [persona.id, persona])),
        [personaOptions],
    );

    const fallbackPersona = personaMap.get(DEFAULT_AUTHOR_ID) || Object.values(AUTHOR_PERSONAS)[0];
    const getPersonaMeta = (personaId) => personaMap.get(personaId) || fallbackPersona;
    const currentPersona = getPersonaMeta(selectedPersona);

    const handleNewChat = async () => {
        await createChat(projectId);
        setShowChatList(false);
        setChatSearch('');
        requestAnimationFrame(() => inputRef.current?.focus());
    };

    const handleStartRename = (chat, e) => {
        e.stopPropagation();
        setEditingChatId(chat._id);
        setEditingTitle(chat.title);
    };

    const handleFinishRename = async () => {
        if (!editingChatId) return;
        const trimmed = editingTitle.trim();
        if (trimmed && trimmed !== currentChat?.title) {
            await renameChat(editingChatId, trimmed);
        }
        setEditingChatId(null);
        setEditingTitle('');
    };

    const handleDeleteChat = async (chatId, e) => {
        e.stopPropagation();
        const chat = chats.find((c) => c._id === chatId);
        if (!chat) return;
        const confirmed = window.confirm(t('ai.deleteChatConfirm', { title: chat.title }));
        if (!confirmed) return;
        await deleteChat(projectId, chatId);
        if (chats.length <= 1) {
            setShowChatList(false);
        }
    };

    const resetMemoryComposer = () => {
        setShowMemoryComposer(false);
        setMemoryKind('canon_correction');
        setMemoryTitle('');
        setMemoryAssumption('');
        setMemoryCorrection('');
    };

    const handleSaveMemory = async () => {
        if (!projectId || !memoryCorrection.trim()) return;

        await createMentorMemory({
            projectId,
            kind: memoryKind,
            title: memoryTitle.trim() || undefined,
            wrongAssumption: memoryKind === 'canon_correction'
                ? memoryAssumption.trim() || undefined
                : undefined,
            correction: memoryCorrection.trim(),
        });

        resetMemoryComposer();
    };

    const handleDeleteMemory = async (memoryId) => {
        const confirmed = window.confirm(t('ai.deleteMemoryConfirm'));
        if (!confirmed) return;
        await deleteMentorMemory({ memoryId });
    };

    const formatChatDate = (timestamp) => {
        return formatChatTimestamp(t, i18n, timestamp);
    };

    const handleQuickPrompt = (prompt) => {
        if (prompt.requiresInput) {
            setQuickPromptInput(prompt);
            setQuickPromptValue('');
            return;
        }
        setInput(prompt.template);
        requestAnimationFrame(() => {
            inputRef.current?.focus();
            syncTextareaHeight(inputRef.current);
        });
    };

    const handleQuickPromptSubmit = () => {
        if (!quickPromptInput || !quickPromptValue.trim()) return;
        const filled = quickPromptInput.template.replace(
            `{{${quickPromptInput.inputField}}}`,
            quickPromptValue.trim()
        );
        setInput(filled);
        setQuickPromptInput(null);
        setQuickPromptValue('');
        requestAnimationFrame(() => {
            inputRef.current?.focus();
            syncTextareaHeight(inputRef.current);
        });
    };

    const thinkingPhaseIcon = thinkingPhase === 'composing' ? '✍️' : thinkingPhase === 'reading' ? '📖' : '🧠';
    const thinkingPhaseLabel = thinkingPhase
        ? t(`ai.thinking.${thinkingPhase}`, { defaultValue: 'Thinking…' })
        : '';

    return (
        <div className="assistant-panel">
            {/* Header */}
            <div className="ap-header">
                <button
                    type="button"
                    className="ap-header-chat-toggle"
                    onClick={() => setShowChatList((prev) => !prev)}
                    title={t("ai.chatHistory")}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                    <span className="ap-chat-count">{chats.length}</span>
                </button>

                <div className="ap-header-center">
                    <span className="ap-header-title">{currentChat?.title || t('ai.newChat')}</span>
                </div>

                <button
                    type="button"
                    className="ap-header-btn"
                    onClick={() => setShowVoiceChat(true)}
                    title={t('accessibleStudio.voice.startSession', { defaultValue: 'Start voice chat' })}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 3a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3Z" />
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                        <line x1="12" y1="19" x2="12" y2="22" />
                    </svg>
                </button>

                <button
                    type="button"
                    className="ap-header-btn"
                    onClick={handleNewChat}
                    title={t("ai.newChat")}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                </button>
            </div>

            {/* Chat List Drawer */}
            <div className={`ap-chat-drawer ${showChatList ? 'open' : ''}`} ref={chatListRef}>
                <div className="ap-drawer-header">
                    <span className="ap-drawer-title">{t("ai.chats")}</span>
                    <button type="button" className="ap-drawer-new" onClick={handleNewChat}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>{t('ai.newChat')}</button>
                </div>

                {chats.length > 3 && (
                    <div className="ap-drawer-search">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                            type="text"
                            placeholder={t("ai.searchChats")}
                            value={chatSearch}
                            onChange={(e) => setChatSearch(e.target.value)}
                        />
                    </div>
                )}

                <div className="ap-drawer-list">
                    {filteredChats.map((chat) => {
                        const persona = getPersonaMeta(chat.selectedPersonaId);
                        const isActive = chat._id === activeChatId;
                        const isEditing = editingChatId === chat._id;
                        return (
                            <div
                                key={chat._id}
                                className={`ap-chat-item ${isActive ? 'active' : ''}`}
                                onClick={() => {
                                    if (isEditing) return;
                                    loadMessages(chat._id);
                                    setShowChatList(false);
                                    setChatSearch('');
                                }}
                                onMouseEnter={() => setHoveredChatId(chat._id)}
                                onMouseLeave={() => setHoveredChatId(null)}
                            >
                                <span className="ap-chat-item-icon">{persona?.emoji}</span>
                                <div className="ap-chat-item-body">
                                    {isEditing ? (
                                        <input
                                            ref={editInputRef}
                                            className="ap-chat-rename-input"
                                            value={editingTitle}
                                            onChange={(e) => setEditingTitle(e.target.value)}
                                            onBlur={handleFinishRename}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') handleFinishRename();
                                                if (e.key === 'Escape') {
                                                    setEditingChatId(null);
                                                    setEditingTitle('');
                                                }
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    ) : (
                                        <span className="ap-chat-item-title">{chat.title}</span>
                                    )}
                                    <span className="ap-chat-item-meta">
                                        {persona?.name} · {formatChatDate(chat.lastMessageAt || chat.updatedAt)}
                                    </span>
                                </div>
                                {hoveredChatId === chat._id && !isEditing && (
                                    <div className="ap-chat-item-actions">
                                        <button
                                            type="button"
                                            className="ap-chat-item-action"
                                            onClick={(e) => handleStartRename(chat, e)}
                                            title={t("common.rename")}
                                        >
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M12 20h9" />
                                                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                                            </svg>
                                        </button>
                                        <button
                                            type="button"
                                            className="ap-chat-item-action danger"
                                            onClick={(e) => handleDeleteChat(chat._id, e)}
                                            title={t("common.delete")}
                                        >
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="3 6 5 6 21 6" />
                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {filteredChats.length === 0 && (
                        <div className="ap-drawer-empty">{t("ai.noChatsFound")}</div>
                    )}
                </div>
            </div>

            {/* Config Bar - Persona + Tier */}
            <div className="ap-config-bar">
                <div className="ap-config-row ap-config-row--primary">
                    <div className="ap-config-persona-wrap">
                        <button
                            className="ap-persona-btn"
                            onClick={() => setShowPersonaMenu(!showPersonaMenu)}
                        >
                            <span className="ap-persona-emoji">{currentPersona?.emoji}</span>
                            <span className="ap-persona-name">{currentPersona?.name}</span>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="6 9 12 15 18 9" />
                            </svg>
                        </button>

                        {showPersonaMenu && (
                            <div className="ap-persona-menu">
                                {personaOptions.map(persona => (
                                    <button
                                        key={persona.id}
                                        type="button"
                                        className={`ap-persona-option ${selectedPersona === persona.id ? 'active' : ''}`}
                                        onClick={() => {
                                            void setSelectedPersona(persona.id);
                                            setShowPersonaMenu(false);
                                        }}
                                    >
                                        <span className="ap-persona-option-emoji">{persona.emoji}</span>
                                        <div className="ap-persona-option-info">
                                            <span className="ap-persona-option-name">{persona.name}</span>
                                            <span className="ap-persona-option-desc">{persona.description}</span>
                                        </div>
                                        {selectedPersona === persona.id && (
                                            <svg className="ap-persona-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {messages.length > 0 && (
                        <button
                            type="button"
                            className="ap-clear-btn"
                            onClick={() => clearChat()}
                            title={t("ai.clearChatHistory")}
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                        </button>
                    )}
                </div>

                <div className="ap-config-row ap-config-row--secondary">
                    <div className="ap-tier-group" role="group" aria-label={t("ai.modelTier")}>
                        {AI_MODEL_TIERS.map((tier) => (
                            <button
                                key={tier.id}
                                type="button"
                                className={`ap-tier-btn ${selectedTier === tier.id ? 'active' : ''}`}
                                onClick={() => void setSelectedTier(tier.id)}
                                title={t(tier.descriptionKey)}
                                aria-pressed={selectedTier === tier.id}
                            >
                                {t(tier.labelKey)}
                            </button>
                        ))}
                    </div>

                    <button
                        type="button"
                        className={`ap-memory-toggle ${allowAgentEdits ? 'active' : ''}`}
                        onClick={() => void setAllowAgentEdits(!allowAgentEdits)}
                        title={allowAgentEdits
                            ? t('ai.editMode.enabled', { defaultValue: 'Agent can edit files' })
                            : t('ai.editMode.disabled', { defaultValue: 'Agent is in read-only mode' })}
                    >
                        <span>{t('ai.editMode.shortLabel', { defaultValue: 'Edits' })}</span>
                        <strong>{allowAgentEdits ? t('common.on', { defaultValue: 'On' }) : t('common.off', { defaultValue: 'Off' })}</strong>
                    </button>

                    {!allowAgentEdits && (
                        <div className="ap-edit-mode-hint">
                            {t('ai.editMode.readOnlyHint')}
                        </div>
                    )}

                    <button
                        type="button"
                        className={`ap-memory-toggle ${showMemoryPanel ? 'active' : ''}`}
                        onClick={() => {
                            setShowMemoryPanel((prev) => !prev);
                            if (showMemoryPanel) {
                                setShowMemoryComposer(false);
                            }
                        }}
                        title={t('ai.memory.title')}
                    >
                        <span>{t('ai.memory.shortTitle')}</span>
                        <strong>{mentorMemory?.length || 0}</strong>
                    </button>
                </div>
            </div>

            <div className="ap-context-shell">
                <button
                    type="button"
                    className={`ap-context-toggle ${showContextPanel ? 'active' : ''}`}
                    onClick={() => setShowContextPanel((prev) => !prev)}
                    aria-expanded={showContextPanel}
                >
                    <div className="ap-context-toggle-copy">
                        <span className="ap-context-toggle-title">
                            {t('ai.memory.contextTitle', { defaultValue: 'Mentor Context' })}
                        </span>
                        <span className="ap-context-toggle-meta">
                            {showContextPanel
                                ? t('ai.memory.contextHide', { defaultValue: 'Hide loaded context details' })
                                : t('ai.memory.contextSummary', {
                                    defaultValue: '{{chapters}} chapters · {{loaded}}/4 sources loaded',
                                    loaded: loadedContextSourceCount,
                                    chapters: manuscriptChapterCount,
                                  })}
                        </span>
                    </div>
                    <svg
                        className={`ap-context-toggle-chevron ${showContextPanel ? 'open' : ''}`}
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.25"
                        aria-hidden="true"
                    >
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </button>

                {showContextPanel && (
                    <div className="ap-context-strip">
                        <div className="ap-context-pill">
                            <span>{t('ai.memory.stats.storyBible')}</span>
                            <strong>{storyBibleStatusLabel}</strong>
                        </div>
                        <div className="ap-context-pill">
                            <span>{t('ai.memory.stats.manuscriptChapters', { defaultValue: 'Manuscript chapters' })}</span>
                            <strong>{manuscriptChapterCount}</strong>
                        </div>
                        <div className="ap-context-pill">
                            <span>{t('ai.memory.stats.activeChapter', { defaultValue: 'Active chapter' })}</span>
                            <strong>{activeChapterLabel}</strong>
                        </div>
                        <div className="ap-context-pill">
                            <span>{t('ai.memory.stats.chapterSummaries')}</span>
                            <strong>{chapterSummaryCount}</strong>
                        </div>
                        <div className="ap-context-pill">
                            <span>{t('ai.memory.stats.savedMemories')}</span>
                            <strong>{savedMemoryCount}</strong>
                        </div>
                        <div className="ap-context-pill">
                            <span>{t('ai.memory.stats.canonUpdates')}</span>
                            <strong>{canonUpdateCount}</strong>
                        </div>
                    </div>
                )}
            </div>

            {showMemoryPanel && (
                <div className="ap-memory-panel">
                    <div className="ap-memory-header">
                        <div>
                            <div className="ap-memory-title">{t('ai.memory.title')}</div>
                            <div className="ap-memory-subtitle">
                                {t('ai.memory.subtitle')}
                            </div>
                        </div>
                        <button
                            type="button"
                            className="ap-memory-add-btn"
                            onClick={() => setShowMemoryComposer((prev) => !prev)}
                        >
                            {showMemoryComposer ? t('common.close') : t('ai.memory.add')}
                        </button>
                    </div>

                    {showMemoryComposer ? (
                        <div className="ap-memory-composer">
                            <div className="ap-memory-kind-row">
                                <button
                                    type="button"
                                    className={`ap-memory-kind-btn ${memoryKind === 'canon_correction' ? 'active' : ''}`}
                                    onClick={() => setMemoryKind('canon_correction')}
                                >
                                    {t('ai.memory.canonCorrection')}
                                </button>
                                <button
                                    type="button"
                                    className={`ap-memory-kind-btn ${memoryKind === 'writing_instruction' ? 'active' : ''}`}
                                    onClick={() => setMemoryKind('writing_instruction')}
                                >
                                    {t('ai.memory.writingInstruction')}
                                </button>
                            </div>
                            <input
                                className="ap-memory-input"
                                placeholder={t('ai.memory.shortLabel')}
                                value={memoryTitle}
                                onChange={(e) => setMemoryTitle(e.target.value)}
                            />
                            {memoryKind === 'canon_correction' ? (
                                <textarea
                                    className="ap-memory-textarea"
                                    rows={2}
                                    placeholder={t('ai.memory.botWrong')}
                                    value={memoryAssumption}
                                    onChange={(e) => setMemoryAssumption(e.target.value)}
                                />
                            ) : null}
                            <textarea
                                className="ap-memory-textarea"
                                rows={3}
                                placeholder={memoryKind === 'canon_correction'
                                    ? t('ai.memory.botRememberInstead')
                                    : t('ai.memory.futureInstruction')}
                                value={memoryCorrection}
                                onChange={(e) => setMemoryCorrection(e.target.value)}
                            />
                            <div className="ap-memory-composer-actions">
                                <button type="button" className="ap-memory-secondary-btn" onClick={resetMemoryComposer}>
                                    {t('common.cancel')}
                                </button>
                                <button
                                    type="button"
                                    className="ap-memory-primary-btn"
                                    disabled={!memoryCorrection.trim()}
                                    onClick={() => void handleSaveMemory()}
                                >
                                    {t('ai.memory.save')}
                                </button>
                            </div>
                        </div>
                    ) : null}

                    {mentorMemory?.length > 0 ? (
                        <div className="ap-memory-list">
                            {mentorMemory.map((memory) => (
                                <div key={memory._id} className="ap-memory-item">
                                    <div className="ap-memory-item-head">
                                        <span className={`ap-memory-kind ap-memory-kind--${memory.kind}`}>
                                            {memory.kind === 'canon_correction' ? t('ai.memory.canonShort') : t('ai.memory.instructionShort')}
                                        </span>
                                        <button
                                            type="button"
                                            className="ap-memory-delete"
                                            onClick={() => void handleDeleteMemory(memory._id)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                    <div className="ap-memory-item-title">{memory.title}</div>
                                    {memory.wrongAssumption ? (
                                        <div className="ap-memory-item-wrong">{t('ai.memory.wrongPrefix')} {memory.wrongAssumption}</div>
                                    ) : null}
                                    <div className="ap-memory-item-correction">{t('ai.memory.usePrefix')} {memory.correction}</div>
                                </div>
                            ))}
                        </div>
                    ) : !showMemoryComposer ? (
                        <div className="ap-memory-empty">
                            {t('ai.memory.empty')}
                        </div>
                    ) : null}
                </div>
            )}

            {/* Messages */}
            <div className="ap-messages">
                {messages.length === 0 && (
                    <div className="ap-welcome">
                        <div className="ap-welcome-icon">{currentPersona?.emoji}</div>
                        <h3 className="ap-welcome-name">{currentPersona?.name}</h3>
                        <p className="ap-welcome-desc">{t('ai.welcomeDesc', { name: currentPersona?.name })}</p>
                        <div className="ap-welcome-hints">
                            <div className="ap-hint">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                                <span>{t('ai.attachPdfOrImage')}</span>
                            </div>
                            <div className="ap-hint">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22 6 12 13 2 6" /></svg>
                                <span>{t('ai.mentionAFile')}</span>
                            </div>
                            <div className="ap-hint">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                                <span>{t('ai.askForStructuralHelp')}</span>
                            </div>
                        </div>
                        <div className="ap-welcome-prompts">
                            <div className="ap-welcome-prompts-label">{t('ai.quickPrompts.title')}</div>
                            <div className="ap-welcome-prompts-grid">
                                {QUICK_PROMPTS.map((prompt) => (
                                    <button
                                        key={prompt.id}
                                        type="button"
                                        className="ap-welcome-prompt-card"
                                        onClick={() => handleQuickPrompt(prompt)}
                                    >
                                        <span className="ap-welcome-prompt-icon">{prompt.icon}</span>
                                        <span className="ap-welcome-prompt-label">{t(prompt.labelKey)}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {messages.map((msg, idx) => {
                    const persona = getPersonaMeta(msg.personaId);
                    return (
                        <div
                            key={`${msg.timestamp || idx}-${idx}`}
                            className={`ap-msg ${msg.role} ${msg.isError ? 'error' : ''}`}
                        >
                            {msg.role === 'assistant' && (
                                <span className="ap-msg-avatar">{persona?.emoji}</span>
                            )}
                            <div className="ap-msg-shell">
                                <div
                                    className="ap-msg-content"
                                    dangerouslySetInnerHTML={{
                                        __html: msg.role === 'assistant'
                                            ? parseMarkdown(msg.content)
                                            : DOMPurify.sanitize(msg.content)
                                    }}
                                />
                                {msg.attachments?.length > 0 && (
                                    <div className="ap-msg-attachments">
                                        {msg.attachments.map((attachment) => (
                                            <div key={`${attachment.storageId || attachment.id}-${attachment.name}`} className="ap-msg-attachment">
                                                {attachment.kind === 'image' ? (
                                                    <img src={attachment.url} alt={attachment.name} className="ap-msg-attachment-img" />
                                                ) : (
                                                    <div className="ap-msg-attachment-pdf">
                                                        <span className="ap-attachment-badge">PDF</span>
                                                        <span>{attachment.name}</span>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}

                {isStreaming && streamingMessage && (
                    <div className="ap-msg assistant streaming">
                        <span className="ap-msg-avatar">{currentPersona?.emoji}</span>
                        <div className="ap-msg-shell">
                            <div
                                className="ap-msg-content"
                                dangerouslySetInnerHTML={{ __html: parseMarkdown(streamingMessage) }}
                            />
                        </div>
                    </div>
                )}

                {isLoading && !isStreaming && (
                    <div className="ap-msg assistant thinking">
                        <span className="ap-msg-avatar">{currentPersona?.emoji}</span>
                        <div className="ap-msg-shell">
                            <div className="ap-msg-content">
                                <div className="ap-thinking-progress">
                                    <div className="ap-thinking-progress-shimmer" />
                                    <span className="ap-thinking-icon">{thinkingPhaseIcon}</span>
                                    <span className="ap-thinking-label">{thinkingPhaseLabel}</span>
                                    {thinkingElapsed > 0 && (
                                        <span className="ap-thinking-timer">{thinkingElapsed}s</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Quick Prompt Input Popover */}
            {quickPromptInput && (
                <div className="ap-prompt-input-popover">
                    <div className="ap-prompt-input-header">
                        <span className="ap-prompt-input-icon">{quickPromptInput.icon}</span>
                        <span>{t(quickPromptInput.labelKey)}</span>
                    </div>
                    <div className="ap-prompt-input-row">
                        <input
                            type="text"
                            className="ap-prompt-input-field"
                            placeholder={t(quickPromptInput.inputPlaceholder)}
                            value={quickPromptValue}
                            onChange={(e) => setQuickPromptValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleQuickPromptSubmit();
                                if (e.key === 'Escape') {
                                    setQuickPromptInput(null);
                                    setQuickPromptValue('');
                                }
                            }}
                            autoFocus
                        />
                        <button
                            type="button"
                            className="ap-prompt-input-go"
                            disabled={!quickPromptValue.trim()}
                            onClick={handleQuickPromptSubmit}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            className="ap-prompt-input-cancel"
                            onClick={() => {
                                setQuickPromptInput(null);
                                setQuickPromptValue('');
                            }}
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {/* Composer */}
            <div className="ap-composer">
                {/* Quick Prompt Pills (above composer, when empty) */}
                {messages.length > 0 && !input.trim() && !isLoading && !isStreaming && !quickPromptInput && (
                    <div className="ap-quick-prompts">
                        {QUICK_PROMPTS.map((prompt) => (
                            <button
                                key={prompt.id}
                                type="button"
                                className="ap-prompt-chip"
                                onClick={() => handleQuickPrompt(prompt)}
                            >
                                <span className="ap-prompt-chip-icon">{prompt.icon}</span>
                                <span>{t(prompt.labelKey)}</span>
                            </button>
                        ))}
                    </div>
                )}
                {pendingAttachments.length > 0 && (
                    <div className="ap-attachments-bar">
                        {pendingAttachments.map((attachment) => (
                            <div key={attachment.id} className="ap-attachment-chip">
                                {attachment.kind === 'image' ? (
                                    <img src={attachment.url} alt={attachment.name} className="ap-attachment-thumb" />
                                ) : (
                                    <span className="ap-attachment-badge">PDF</span>
                                )}
                                <span className="ap-attachment-name">{attachment.name}</span>
                                <button
                                    type="button"
                                    className="ap-attachment-remove"
                                    onClick={() => {
                                        setPendingAttachments((prev) => {
                                            const next = prev.filter((item) => item.id !== attachment.id);
                                            revokeAttachmentPreview(attachment);
                                            return next;
                                        });
                                    }}
                                >
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="ap-input-row">
                    {showMentionMenu && fileMentions.length > 0 && (
                        <div className="ap-mention-menu">
                            {fileMentions.map((file, idx) => (
                                <button
                                    key={file.path}
                                    className={`ap-mention-option ${idx === mentionIndex ? 'active' : ''}`}
                                    onClick={() => handleMentionSelect(file)}
                                    type="button"
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                    </svg>
                                    <span className="ap-mention-path">{file.path}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    <button
                        type="button"
                        className="ap-composer-btn attach"
                        onClick={() => fileInputRef.current?.click()}
                        title={t("ai.uploadImageOrPdf")}
                        disabled={!activeChatId || isUploadingAttachment}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                        </svg>
                    </button>

                    <textarea
                        ref={inputRef}
                        className="ap-textarea"
                        placeholder={t('ai.messagePlaceholder', { name: currentPersona?.name })}
                        value={input}
                        onChange={(e) => {
                            const value = e.target.value;
                            setInput(value);
                            syncTextareaHeight(e.target);

                            const cursor = e.target.selectionStart ?? value.length;
                            const match = value.slice(0, cursor).match(/@([^\s]*)$/);
                            if (match) {
                                setMentionQuery(match[1]);
                                setShowMentionMenu(true);
                                setMentionIndex(0);
                            } else {
                                setShowMentionMenu(false);
                            }
                        }}
                        onPaste={(e) => {
                            void handleComposerPaste(e);
                        }}
                        onKeyDown={handleKeyDown}
                        rows={1}
                    />

                    {(isLoading || isStreaming) ? (
                        <button
                            type="button"
                            className="ap-composer-btn send stop"
                            onClick={stopGeneration}
                            title={t("ai.stopGenerating")}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                <rect x="6" y="6" width="12" height="12" rx="2" />
                            </svg>
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="ap-composer-btn send"
                            onClick={handleSend}
                            disabled={(!input.trim() && pendingAttachments.length === 0) || !activeChatId}
                            title={t("ai.send")}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 2L11 13" />
                                <path d="M22 2L15 22L11 13L2 9L22 2Z" />
                            </svg>
                        </button>
                    )}

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,application/pdf"
                        multiple
                        hidden
                        onChange={(event) => {
                            void handleFileInputChange(event);
                        }}
                    />
                </div>
            </div>

            {showVoiceChat && (
                <div className="ap-voice-modal">
                    <div className="ap-voice-backdrop" onClick={() => setShowVoiceChat(false)} />
                    <div className="ap-voice-dialog" role="dialog" aria-modal="true">
                        <RealtimeVoiceChat
                            projectId={projectId}
                            chatId={activeChatId}
                            personaId={selectedPersona}
                            activeFilePath={activeTabPath}
                            embedded
                            title={t('accessibleStudio.voice.inlineTitle', { defaultValue: 'Voice chat with your mentor' })}
                            description={t('accessibleStudio.voice.inlineDescription', {
                                defaultValue: 'Use live voice guidance without leaving the Writing Studio. The transcript will sync back into this mentor chat.',
                            })}
                            onClose={() => setShowVoiceChat(false)}
                            onTranscriptSaved={() => {
                                if (activeChatId) {
                                    void loadMessages(activeChatId);
                                }
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default AssistantPanel;
